import { alert } from 'actions/flash'
import { getValidation } from 'actions/validations'
import { useBrameIntegration } from 'api/integrationsConfig'
import {
  ContentBlock,
  ContentBlockTypes,
  landingPageThemeApi,
  Settings,
} from 'api/landingPageTheme'
import { storedValueApi } from 'api/storedValue'
import { isError } from 'api/utils'
import ConfirmModal from 'components/ConfirmModal'
import { ImagePickerValue } from 'components/ImagePicker'
import { TabT } from 'components/Tabs/InnerTabs'
import palette from 'constants/palette'
import Formsy from 'formsy-react'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import sortBy from 'lodash/sortBy'
import uniqueId from 'lodash/uniqueId'
import { ValidationTypes } from 'models/Validation'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Content from 'scenes/Cms/Content/AppHomePage/Content'
import StyleSettings from 'scenes/Cms/Content/AppHomePage/Style'
import { getDefaultBannerStyles } from 'scenes/Cms/Content/Banners/helpers'
import { getDefaultMenuCategoryStyles } from 'scenes/Cms/Content/MenuCategory/helpers'
import { getUrlValue } from 'scenes/Cms/helper'
import { selectApp } from 'selectors/app'
import {
  deleteContentBlock as deleteAppLandingPageContentBlock,
  reset as resetAppLandingPageTheme,
  selectActiveContentBlocks,
  selectAppLandingPageTheme,
  selectExpiredContentBlocks,
  updateCarouselSlide as updateAppLandingPageCarouselSlide,
  updateContentBlock as updateAppLandingPageContentBlock,
  updateSettings as updateAppLandingPageSettings,
} from 'slices/appLandingPageTheme'
import { initialAppLandingPageThemeState } from 'slices/appLandingPageTheme/initialState'
import type {
  AppLandingPageTheme,
  ContentBlockData,
  FeaturedContentSlideData,
  FormContentBlockData,
} from 'slices/appLandingPageTheme/types'
import { getImageUrlFromBackend } from './Content/helper'
import Footer from './Footer'
import {
  getDefaultBlockData,
  getSubmitPayload,
  hasImage,
  transformBackendTargetingToUI,
  withButtonColorDefaults,
} from './helpers'
import Sidebar from './Sidebar'

export type useAppHomepageT = {
  tab: TabT[]
}

export enum Tabs {
  StyleSettings = 'app_homepage_style_settings',
  Content = 'app_homepage_content',
}

const t = buildTranslate('cms.content.app_home_page')

export function useAppHomePage() {
  const app = useSelector(selectApp)
  const appId = app?.id
  const dispatch = useDispatch()
  const appLandingPageTheme = useSelector(selectAppLandingPageTheme)
  const activeContentBlocks = useSelector(selectActiveContentBlocks)
  const expiredBlocks = useSelector(selectExpiredContentBlocks)
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isFormValid, setIsFormValid] = useState(true)
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)
  const [autoEditBlockId, setAutoEditBlockId] = useState<string | null>(null)
  const [generatedBlocks, setGeneratedBlocks] = useState<{
    [blockId: string]: ContentBlockData
  }>({})
  const defaultSettings = useDefaultLandingPageThemeSettings()

  const [blockOrder, setBlockOrder] = useState<string[]>([])

  const featuredContentCarouselEnabled = useFlag(
    'featured-content-carousel-mx',
    false
  )

  const { availableTypes, isLoading: availableTypesIsLoading } =
    useAvailableContentBlockTypes()
  const { data: landingPageThemeData, isFetching } =
    landingPageThemeApi.useGetLandingPageThemeQuery(appId)
  const landingPageTheme = landingPageThemeData?.app_landing_page_theme ?? null
  const [updateLandingPageTheme, { isLoading: isFormSubmitting }] =
    landingPageThemeApi.useUpdateLandingPageThemeMutation()

  async function handleSubmit() {
    const payload = getSubmitPayload({
      blockOrder,
      formModel: appLandingPageTheme,
      landingPageTheme,
      featuredContentCarouselEnabled,
    })

    const response = await updateLandingPageTheme({
      id: appId,
      landing_page_theme: payload,
    })
    setIsConfirmModalVisible(false)

    if (isError(response)) {
      dispatch(
        alert({
          key: 'danger',
          message: t('submit_error'),
          timeout: 5,
        })
      )
      return
    }

    dispatch(
      alert({
        key: 'success',
        message: t('submit_success'),
        timeout: 5,
      })
    )

    setGeneratedBlocks({})
  }
  const isImageUploading = blockOrder.some(blockId => {
    const contentBlock = appLandingPageTheme.contentBlocks[blockId]
    return 'image' in contentBlock && contentBlock.image.uploading
  })

  const sidebar = (type: 'content' | 'style') => (
    <Sidebar
      isLoading={isFetching}
      disabled={isImageUploading || isFormSubmitting || !isFormValid}
      onPublish={
        type === 'content' ? () => setIsConfirmModalVisible(true) : () => {}
      }
      type={type}
    />
  )
  const footer = (type: 'content' | 'style') => (
    <Footer
      isLoading={isFetching}
      disabled={isImageUploading || isFormSubmitting || !isFormValid}
      onPublish={
        type === 'content' ? () => setIsConfirmModalVisible(true) : () => {}
      }
      type={type}
    />
  )

  function updateContentBlock(blockId: string, data: ContentBlockData) {
    dispatch(
      updateAppLandingPageContentBlock({
        key: blockId,
        data,
      })
    )
  }

  function deleteContentBlock(blockId: string) {
    dispatch(
      deleteAppLandingPageContentBlock({
        key: blockId,
      })
    )
  }

  function createContentBlock(key: string, data: ContentBlockData) {
    dispatch(
      updateAppLandingPageContentBlock({
        key,
        data,
      })
    )
  }

  function updateSettings(settings: Partial<Settings>) {
    dispatch(updateAppLandingPageSettings(settings))
  }

  function getFormCarouselFeaturedContents(block: any): {
    [slideId: string]: FeaturedContentSlideData
  } {
    if (!('featured_contents' in block) || !block.featured_contents) {
      return {}
    }

    // If it's already a hash (from form state), return as-is
    if (
      typeof block.featured_contents === 'object' &&
      !Array.isArray(block.featured_contents)
    ) {
      return block.featured_contents
    }

    // If it's an array (from API), convert to hash
    const featuredContentsArray = Array.isArray(block.featured_contents)
      ? block.featured_contents
      : Object.values(block.featured_contents)

    return featuredContentsArray.reduce((acc: any, slide: any) => {
      const slideId =
        slide.id?.toString() || `slide-${Date.now()}-${Math.random()}`

      // Extract URL similar to how featured_content blocks do it
      const url = 'url' in slide.settings ? slide.settings.url : ''
      const urlValue = getUrlValue(url)

      // Extract image info similar to other blocks
      const imageUrl =
        'image_urls' in slide
          ? getImageUrlFromBackend(slide.image_urls)
          : undefined
      const image: ImagePickerValue = {
        id: null,
        url: imageUrl || undefined,
        uploading: false,
      }

      acc[slideId] = {
        id: slide.id,
        type: slide.type,
        settings: slide.settings,
        image,
        url: urlValue,
        position: slide.position || 1,
      }

      return acc
    }, {})
  }

  const resetForm = useCallback(
    ({
      contentBlocks,
      settings,
    }: {
      contentBlocks: Array<ContentBlock>
      settings: Settings
    }) => {
      // Captured before the per-block destructure below shadows `settings`; the button
      // backfill passes it to getDefaultBlockData as the theme settings.
      const themeSettings = settings
      const formattedContentBlocks: FormContentBlockData = contentBlocks.reduce(
        (acc, block) => {
          const { id, type, settings } = block

          if (!availableTypes.includes(type)) return acc

          const transformedSettings = transformBackendTargetingToUI(settings, {
            start_at: block.start_at,
            end_at: block.end_at,
            target_segments: block.target_segments,
          })

          const {
            audience_type,
            availability_type,
            segment_id,
            start_at,
            end_at,
            target_segments,
            ...cleanSettings
          } = transformedSettings

          acc[id] = {
            id: block.id,
            type,
            position: block.position,
            settings: withButtonColorDefaults(
              type,
              cleanSettings,
              app?.primary_color ?? '',
              themeSettings
            ),
            audience_type,
            availability_type,
            segment_id,
            start_at,
            end_at,
            target_segments,
          }

          if (hasImage(type)) {
            const imageUrl =
              'image_urls' in block
                ? getImageUrlFromBackend(block.image_urls)
                : undefined
            const image: ImagePickerValue = {
              id: null,
              url: imageUrl || undefined,
              uploading: false,
            }

            acc[id] = {
              ...acc[id],
              image,
            }
          }
          if (['featured_content', 'personalized_text'].includes(type)) {
            const url = 'url' in settings ? settings.url : undefined
            const urlValue = getUrlValue(url)
            acc[id] = {
              ...acc[id],
              url: urlValue,
            }
          }

          if (type === 'featured_content_carousel') {
            // Handle carousel blocks with featured_contents object
            acc[id] = {
              ...acc[id],
              featured_contents: getFormCarouselFeaturedContents(block),
            }
          }

          return acc
        },
        {}
      )

      const finalContentBlocks = { ...formattedContentBlocks }
      const missingGeneratedBlocks: string[] = []

      Object.keys(generatedBlocks).forEach(blockId => {
        if (!finalContentBlocks[blockId]) {
          finalContentBlocks[blockId] = generatedBlocks[blockId]
          missingGeneratedBlocks.push(blockId)
        }
      })

      dispatch(
        resetAppLandingPageTheme({
          contentBlocks: finalContentBlocks,
          settings,
        })
      )

      const filteredContentBlocks = contentBlocks.filter(block =>
        Object.keys(formattedContentBlocks).includes(`${block.id}`)
      )

      const serverBlockOrder = sortBy(filteredContentBlocks, 'position').map(
        block => `${block.id}`
      )
      const finalBlockOrder = [...serverBlockOrder, ...missingGeneratedBlocks]

      setBlockOrder(finalBlockOrder)
    },
    [app?.primary_color, availableTypes, dispatch, generatedBlocks]
  )

  function onNewBlock(type: ContentBlockTypes, preSelectedGameId?: string) {
    const newBlock = getDefaultBlockData(
      type,
      app.primary_color,
      appLandingPageTheme.settings
    )
    if (!newBlock) {
      return null
    }

    // Pre-select game if gameId is provided and this is a game block
    if (type === 'game' && preSelectedGameId && 'settings' in newBlock) {
      newBlock.settings = {
        ...newBlock.settings,
        brame_campaign_id: preSelectedGameId,
      }
    }

    const newId = uniqueId('content-')
    createContentBlock(newId, newBlock)

    // Add to generated blocks tracking so it persists through resetForm
    setGeneratedBlocks(prev => ({ ...prev, [newId]: newBlock }))

    setBlockOrder(blockOrder => [...blockOrder, newId])

    // If this is a pre-selected game block, mark it for auto-scrolling
    if (type === 'game' && preSelectedGameId) {
      setAutoEditBlockId(newId)
    }

    return newId
  }

  // Auto-create game block when gameId parameter is present
  // Only run this AFTER server data has loaded to avoid conflicts with resetForm
  useEffect(() => {
    // Don't process URL params until both the types are loaded AND we have server data
    if (availableTypesIsLoading || !landingPageTheme) {
      return
    }

    // Parse gameId from hash URL parameters (e.g., #app_homepage_content?gameId=123)
    const hash = window.location.hash
    const hashParts = hash.split('?')
    if (hashParts.length < 2) {
      return
    }

    const urlParams = new URLSearchParams(hashParts[1])
    const gameId = urlParams.get('gameId')
    const blockId = urlParams.get('blockId')

    if (gameId) {
      // Auto-create a game block with the specified game ID
      onNewBlock('game', gameId)
    } else if (blockId) {
      // Scroll to existing block with the specified block ID
      setAutoEditBlockId(blockId)
    }
    // Clean up the URL parameter to prevent re-scrolling on page refresh
    const newUrl = window.location.pathname + hashParts[0]
    window.history.replaceState({}, '', newUrl)
  }, [availableTypesIsLoading, landingPageTheme, onNewBlock])

  // Auto-scroll to block when marked for scrolling (e.g., when created from "Add to homepage")
  useEffect(() => {
    if (
      autoEditBlockId &&
      appLandingPageTheme?.contentBlocks?.[autoEditBlockId]
    ) {
      requestAnimationFrame(() => {
        const blockElement = document.querySelector(
          `[data-block-id="${autoEditBlockId}"]`
        )
        if (blockElement) {
          blockElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      })

      setAutoEditBlockId(null) // Clear the flag
    }
  }, [autoEditBlockId, appLandingPageTheme?.contentBlocks])

  useEffect(() => {
    if (availableTypesIsLoading) return
    const settings = landingPageTheme?.settings ?? defaultSettings
    const contentBlocks = landingPageTheme?.content_blocks ?? []

    resetForm({
      contentBlocks,
      settings,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingPageTheme, defaultSettings, availableTypesIsLoading]) // resetForm intentionally excluded to avoid infinite loop

  useEffect(() => {
    if (
      availableTypesIsLoading ||
      !landingPageTheme ||
      Object.keys(generatedBlocks).length === 0
    )
      return

    const settings = landingPageTheme?.settings ?? defaultSettings
    const contentBlocks = landingPageTheme?.content_blocks ?? []

    resetForm({
      contentBlocks,
      settings,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    generatedBlocks,
    availableTypesIsLoading,
    landingPageTheme,
    defaultSettings,
  ]) // resetForm intentionally excluded

  useEffect(() => {
    async function fetchData() {
      dispatch(getValidation({ type: ValidationTypes.AppHomePage }))
    }

    fetchData()
  }, [dispatch])

  function handleContentValidSubmit() {
    setIsEditModalVisible(false)
    setIsFormValid(true)
  }

  function handleContentInvalidSubmit() {
    setIsFormValid(false)
    dispatch(
      alert({
        key: 'danger',
        message: t('validation_error'),
        timeout: 5,
      })
    )
  }

  function handleStyleValidSubmit() {
    setIsConfirmModalVisible(true)
    setIsFormValid(true)
  }

  function handleStyleInValidSubmit() {
    dispatch(
      alert({
        key: 'danger',
        message: t('validation_error'),
        timeout: 5,
      })
    )
  }

  const tab: TabT[] = [
    {
      key: Tabs.Content,
      title: t('tab_title'),
      subtabs: [
        {
          key: Tabs.Content,
          title: t('content.subtab_title'),
          component: (
            <Formsy
              onChange={(
                data: AppLandingPageTheme['contentBlocks'],
                formChanged: boolean
              ) => {
                if (!formChanged) return
                blockOrder.forEach(blockId => {
                  const incomingData = data[blockId]
                  if (!incomingData) return

                  // Create a copy to potentially modify
                  let processedData = { ...incomingData }

                  // Auto-clear fields based on radio button changes
                  if (
                    'availability_type' in processedData &&
                    processedData.availability_type === 'always_available'
                  ) {
                    // Clear datetime fields when switching to always available
                    processedData.start_at = undefined
                    processedData.end_at = undefined
                  }

                  if (
                    'audience_type' in processedData &&
                    processedData.audience_type === 'all_customers'
                  ) {
                    // Clear segment_id when switching to all customers
                    processedData.segment_id = undefined
                  }

                  updateContentBlock(blockId, processedData)
                })
              }}
              onValidSubmit={handleContentValidSubmit}
              onInvalidSubmit={handleContentInvalidSubmit}
            >
              <Content
                activeContentBlocks={activeContentBlocks}
                allContentBlocks={appLandingPageTheme.contentBlocks}
                expiredBlocks={expiredBlocks}
                sidebar={sidebar('content')}
                footer={footer('content')}
                blockOrder={blockOrder}
                setBlockOrder={setBlockOrder}
                isFetching={isImageUploading || isFetching}
                onNewBlock={onNewBlock}
                updateContentBlock={updateContentBlock}
                deleteContentBlock={deleteContentBlock}
                isEditModalOpen={isEditModalVisible}
                setIsEditModalOpen={setIsEditModalVisible}
                selectedBlockId={selectedBlockId}
                setSelectedBlockId={setSelectedBlockId}
                setIsFormValid={setIsFormValid}
              />
              <ConfirmModal
                title={t('modal.publish_changes')}
                description={t('modal.you_are_publishing')}
                confirmText={t('modal.publish')}
                isOpen={isConfirmModalVisible}
                isLoading={isFormSubmitting}
                onClose={() => setIsConfirmModalVisible(false)}
                onConfirm={handleSubmit}
              />
            </Formsy>
          ),
        },
        {
          key: Tabs.StyleSettings,
          title: t('style_settings.subtab_title'),
          component: (
            <Formsy
              onChange={(
                data: AppLandingPageTheme['settings'],
                formChanged: boolean
              ) => {
                if (!formChanged) return
                updateSettings(data)
              }}
              onValidSubmit={handleStyleValidSubmit}
              onInvalidSubmit={handleStyleInValidSubmit}
            >
              <StyleSettings
                sidebar={sidebar('style')}
                footer={footer('style')}
                isFetching={isFetching}
                settings={appLandingPageTheme.settings}
              />
            </Formsy>
          ),
        },
      ],
    },
  ]

  const carouselHook = useFeaturedContentCarousel()

  return {
    tab,
    ...carouselHook,
  }
}

export const useFeaturedContentCarousel = () => {
  const app = useSelector(selectApp)
  const dispatch = useDispatch()
  const appLandingPageTheme = useSelector(selectAppLandingPageTheme)

  function addSlideToCarousel(carouselId: string) {
    const carouselBlock = appLandingPageTheme.contentBlocks[carouselId]

    if (!carouselBlock || carouselBlock.type !== 'featured_content_carousel') {
      // Invalid carousel block
      return
    }

    const newSlide = getDefaultBlockData(
      'featured_content',
      app.primary_color,
      appLandingPageTheme.settings
    )
    if (!newSlide || newSlide.type !== 'featured_content') return

    const newId = uniqueId('slide-')

    // Get current slide order for this carousel from the featured_contents
    const currentSlides = Object.keys(carouselBlock.featured_contents || {})
    const newPosition = currentSlides.length + 1

    // Create a new featured content slide for the carousel using form state format
    const newFeaturedContentSlide: FeaturedContentSlideData = {
      id: newId,
      type: 'featured_content',
      settings: newSlide.settings,
      image: newSlide.image,
      url: newSlide.url,
      position: newPosition,
    }

    // Update the carousel block to include the new slide in featured_contents
    dispatch(
      updateAppLandingPageCarouselSlide({
        carouselId,
        slideId: newId,
        slideData: newFeaturedContentSlide,
      })
    )
  }

  function removeSlideFromCarousel(carouselId: string, slideId: string) {
    const carouselBlock = appLandingPageTheme.contentBlocks[carouselId]
    if (!carouselBlock || carouselBlock.type !== 'featured_content_carousel') {
      // Invalid carousel block
      return
    }

    const updatedCarouselBlock = {
      ...carouselBlock,
      featured_contents: Object.keys(
        carouselBlock.featured_contents || {}
      ).reduce((acc, key) => {
        if (key !== slideId) {
          acc[key] = carouselBlock.featured_contents?.[key]
        }
        return acc
      }, {} as { [slideId: string]: FeaturedContentSlideData }),
    }

    // Update positions for remaining slides
    const remainingSlides = Object.keys(updatedCarouselBlock.featured_contents)
    const reorderedFeaturedContents = remainingSlides.reduce(
      (acc, slideId, index) => {
        acc[slideId] = {
          ...updatedCarouselBlock.featured_contents[slideId],
          position: index + 1,
        }
        return acc
      },
      {} as { [slideId: string]: FeaturedContentSlideData }
    )

    const finalCarouselBlock = {
      ...updatedCarouselBlock,
      featured_contents: reorderedFeaturedContents,
    }

    dispatch(
      updateAppLandingPageContentBlock({
        key: carouselId,
        data: finalCarouselBlock,
      })
    )
  }

  function updateSlide(
    carouselId: string,
    slideId: string,
    data: FeaturedContentSlideData
  ) {
    const carouselBlock = appLandingPageTheme.contentBlocks[carouselId]
    if (!carouselBlock || carouselBlock.type !== 'featured_content_carousel') {
      // Invalid carousel block
      return
    }

    dispatch(
      updateAppLandingPageCarouselSlide({
        carouselId,
        slideId,
        slideData: data,
      })
    )
  }

  function updateCarousel(
    carouselId: string,
    updatedCarouselBlock: ContentBlockData
  ) {
    dispatch(
      updateAppLandingPageContentBlock({
        key: carouselId,
        data: updatedCarouselBlock,
      })
    )
  }

  function reorderSlides(carouselId: string, newSlideOrder: string[]) {
    const carouselBlock = appLandingPageTheme.contentBlocks[carouselId]
    if (!carouselBlock || carouselBlock.type !== 'featured_content_carousel') {
      // Invalid carousel block
      return
    }

    // Update positions for all slides based on new order
    const updatedFeaturedContents = { ...carouselBlock.featured_contents }
    newSlideOrder.forEach((slideId, index) => {
      if (updatedFeaturedContents[slideId]) {
        updatedFeaturedContents[slideId] = {
          ...updatedFeaturedContents[slideId],
          position: index + 1,
        }
      }
    })

    const updatedCarouselBlock = {
      ...carouselBlock,
      featured_contents: updatedFeaturedContents,
    }

    dispatch(
      updateAppLandingPageContentBlock({
        key: carouselId,
        data: updatedCarouselBlock,
      })
    )
  }

  return {
    addSlideToCarousel,
    updateSlide,
    updateCarousel,
    removeSlideFromCarousel,
    reorderSlides,
  }
}

export const useAvailableContentBlockTypes = (): {
  availableTypes: ContentBlockTypes[]
  isLoading: boolean
  gameContentBlocksAvailable: boolean
} => {
  const merchant = useCurrentMerchant()
  const { app_ordering_enabled } = merchant || {}
  const { brameIntegrationEnabled, isLoading: integrationsLoading } =
    useBrameIntegration()

  const personalizedTextEnabled = useFlag(
    'personalized-content-block-mx',
    false
  )
  const featuredContentCarouselEnabled = useFlag(
    'featured-content-carousel-mx',
    false
  )
  const gamificationContentBlocksEnabled = useFlag(
    'gamification-content-blocks-enabled',
    false
  )

  // Game content blocks are only available when both the feature flag AND brame integration are enabled
  const gameContentBlocksAvailable =
    gamificationContentBlocksEnabled && brameIntegrationEnabled
  const orderingSpecificBlocks: ContentBlockTypes[] = [
    'recent_orders',
    'menu_categories',
  ]

  const availableTypes: ContentBlockTypes[] = [
    'featured_content',
    'rewards',
    ...(personalizedTextEnabled ? ['personalized_text' as const] : []),
    ...(featuredContentCarouselEnabled
      ? ['featured_content_carousel' as const]
      : []),
    ...(app_ordering_enabled ? orderingSpecificBlocks : []),
    ...(gamificationContentBlocksEnabled ? ['game' as const] : []),
    'external_form',
  ]

  const { data, isLoading } = storedValueApi.useGetConfigQuery()
  if (data?.state === 'active') {
    availableTypes.push('stored_value_balance')
  }

  if (merchant?.loyalty_transition_settings?.eligible) {
    availableTypes.push('missing_loyalty_prompt')
  }

  return {
    availableTypes,
    isLoading: isLoading || integrationsLoading,
    gameContentBlocksAvailable,
  }
}

export const useDefaultLandingPageThemeSettings = (): Settings => {
  const app = useSelector(selectApp)
  return useMemo(
    () =>
      app
        ? {
            link_color: app.primary_color,
            background_color: palette.white,
            header_text_color: palette.grey90,
            body_text_color: palette.grey70,
            ...getDefaultBannerStyles(),
            menu_category_page: getDefaultMenuCategoryStyles(app.primary_color),
          }
        : initialAppLandingPageThemeState.settings,
    [app]
  )
}
